import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { Item, ItemDeliveryStats, PaymentStatus, operationHelpers, useOperationBuilders, useOperationService, usePriceOutlineHooks, usePricesHooks } from "../../operations"
import { useHoardBuilders } from "../builders/hoardBuilders"
import { TableName } from "../../../tables/types/types"
import { useHoardActions } from "../actions/hoardActions"
import { useStockService } from "../../stock"
import { useHoardLoaders } from "../loaders/hoardLoaders"
import { useSaleOperationService } from "./sale-operation-service/saleOperationService"
import { HoardOrder, HoardType, SpecialDiscountItem } from "../state/types"
import { useAdjustmentNoteService, usePaymentTypeHooks, useTaxHooks } from "../../administration"
import { O } from "../../../features/ui"
import { DeliveryStatus, useDeliveryOrderLoaders, useDeliveryOrderService } from "../../deliveries"
import { round } from "../../../utils/typeUtils"
import { hoardHelpers, useHoardHooks } from "../hooks/hoardHooks"
import { DataTypeEvent } from "../../../features/events"
import { FiscalDocumentStatus } from "../../fiscal-documents"
import { useSystemStateHooks } from "../../system"
import { useServer } from "../../../services/server"

// Entrega de un acopio financiero que supera el crédito y cuyo excedente se cobra antes de emitirla
export type PendingFinancialDelivery = {
    deliveryOrderData: Data2
    itemsData: Partial<Item>[]
}

export const useHoardService = () => {
    const { generateCode, formatPrice } = useDataTypesHooks()
    const { userId } = useSystemStateHooks()
    const pricesHooks = usePricesHooks()
    const { getPriceOutline } = usePriceOutlineHooks()
    const { getIncreasedHoard, getExtendedValue, getClosingCredit } = useHoardHooks()
    const { getPaymentTypeYield } = usePaymentTypeHooks()
    const { getVat21Tax } = useTaxHooks()
    const { describeCode } = operationHelpers

    const { buildHoard } = useHoardBuilders()
    const { buildItem, buildItemDeliveryStats } = useOperationBuilders()

    const hoardLoaders = useHoardLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()

    const { buildSaleOperationItems, buildEditedSaleOperationItems, updateReservedStock, archiveSaleOperation, updateSaleOperationProps, applyAdjustmentNote } = useSaleOperationService()
    const { updateDeliveryStats } = useOperationService()
    const { reserveStock, enableStock } = useStockService()
    const { emitDeliveryOrder } = useDeliveryOrderService()
    const { createDebitNote, createUnusedHoardCreditNote } = useAdjustmentNoteService()
    const { correctHoardPrice: correctServerHoardPrice } = useServer()

    const { saveHoard } = useHoardActions()

    const addEvent = (hoard: HoardOrder, description: string): DataTypeEvent[] => [
        ...(hoard.events || []),
        { date: new Date().toISOString(), description, userId }
    ]

    const createHoard = async (
        hoardData: Data2,
        itemsData: Data2[],
        specialDiscountItems?: SpecialDiscountItem[],
        keepItemsCustomerTypeYield = false
    ) => {
        const code = await generateCode(TableName.HOARD_ORDERS)
        const vatTaxYield = hoardData.type === HoardType.FINANCIAL ? getVat21Tax().yield : undefined
        // Al mantener los precios de un presupuesto, cada ítem conserva su ajuste de lista en vez de tomar el de la lista actual.
        // Los ítems de un presupuesto traen sus estadísticas de entrega: se descartan para que salgan de la cantidad final
        const items = buildEditedSaleOperationItems(itemsData, keepItemsCustomerTypeYield ? undefined : hoardData.customerTypeId! as string)
        const paymentTypeYield = getPaymentTypeYield(hoardData.paymentTypeId! as string)
        const pricesList = await pricesHooks.product.pricesList(hoardData.customerTypeId! as string)
        specialDiscountItems?.forEach(item => {
            pricesList[item.productId] = item.finalPrice
        })
        const hoard = buildHoard({
            ...hoardData,
            code,
            vatTaxYield,
            items,
            paymentTypeYield,
            pricesList,
            specialDiscountItems
        })

        for (const { productId, lotId, quantity } of items) {
            await reserveStock(productId, hoard.branchId, quantity, lotId)
        }

        return saveHoard(hoard)
    }

    const updateHoard = async (
        hoardData: Data2,
        itemsData: Data2[],
        specialDiscountItems?: SpecialDiscountItem[]
    ) => {
        const { loadHoard } = hoardLoaders()
        const loadedHoard = await loadHoard(hoardData.id! as string, true)

        const isFinancial = loadedHoard!.type === HoardType.FINANCIAL
        const items = isFinancial ? buildSaleOperationItems(itemsData) : buildEditedSaleOperationItems(itemsData)
        const paymentTypeYield = hoardData.paymentTypeId === loadedHoard?.paymentTypeId ?
            loadedHoard?.paymentTypeYield :
            getPaymentTypeYield(hoardData.paymentTypeId! as string)

        // Si no se pasan descuentos especiales se conservan los del acopio; para quitarlos hay que pasar []
        const updatedPricesList = { ...loadedHoard!.pricesList }
        if (specialDiscountItems) {
            loadedHoard!.specialDiscountItems?.forEach(item => {
                updatedPricesList[item.productId] = item.listPrice
            })
            specialDiscountItems.forEach(item => {
                updatedPricesList[item.productId] = item.finalPrice
            })
        }

        const hoard = buildHoard({
            ...loadedHoard,
            ...hoardData,
            items,
            paymentTypeYield,
            pricesList: updatedPricesList,
            specialDiscountItems: specialDiscountItems ?? loadedHoard!.specialDiscountItems,
            internalNotes: hoardData.internalNotes ?? loadedHoard?.internalNotes ?? []
        })

        if (isFinancial && loadedHoard?.customerTypeId !== hoardData.customerTypeId) {
            await updateReservedStock(loadedHoard!.items, loadedHoard!.branchId, [], hoard.branchId)
            hoard.items = []
        }

        const savedHoard = await saveHoard(hoard)
        if (!isFinancial) {
            await updateReservedStock(loadedHoard!.items, loadedHoard!.branchId, savedHoard.items, savedHoard.branchId)
        }

        return savedHoard
    }

    const findInitialItem = (initialItems: Item[], newItem: Item): O<Item> =>
        initialItems.find(initialItem => initialItem.productId === newItem.productId && (!initialItem.lotId || !newItem.lotId || initialItem.lotId === newItem.lotId))

    const mergeItems = (initialItems: Item[], newItems: Item[]): Item[] => {
        let mergedItems = initialItems.map(initialItem => ({ ...initialItem }))
        
        newItems.forEach(newItem => {
            const initialItem = findInitialItem(initialItems, newItem)
            if (initialItem) {
                const initialDiscountYield = initialItem.quantity * (initialItem.specialDiscountYield || 0)
                const newDiscountYield = newItem.quantity * (newItem.specialDiscountYield || 0)
                const specialDiscountYield = (initialDiscountYield + newDiscountYield) / (initialItem.quantity + newItem.quantity)

                const newMergedItem: Item = {
                    ...initialItem,
                    lotId: initialItem.lotId || newItem.lotId,
                    quantity: initialItem.quantity + newItem.quantity,
                    specialDiscountYield,
                    deliveryStats: buildItemDeliveryStats({
                        toBeEmitted: initialItem.deliveryStats.toBeEmitted + newItem.deliveryStats.toBeEmitted,
                        pending: initialItem.deliveryStats.pending + newItem.deliveryStats.pending,
                        emitted: initialItem.deliveryStats.emitted + newItem.deliveryStats.emitted
                    })
                }
                mergedItems = mergedItems.filter(mergedItem => mergedItem.id !== initialItem.id).concat(newMergedItem)
            } else {
                mergedItems.push(newItem)
            }
        })

        return mergedItems
    }

    const emitFinancialDeliveryOrder = async (
        hoardId: string,
        deliveryOrderData: Data2,
        hoardItemsData: Partial<Item>[],
    ) => {
        const { loadHoard } = hoardLoaders()
        // Se lee de la base: el valor y lo pagado definen si la entrega amplía el acopio
        const loadedHoard = (await loadHoard(hoardId, true))!

        const items = hoardItemsData.map(itemData => buildItem({
            ...itemData,
            deliveryStats: buildItemDeliveryStats({
                toBeEmitted: itemData.quantity,
                pending: 0,
                emitted: itemData.quantity
            })
        }))
        const newItems = mergeItems(loadedHoard.items, items)

        await updateSaleOperationProps(TableName.HOARD_ORDERS, hoardId, { items: newItems })

        // Si la entrega supera el crédito pendiente (el usuario lo confirmó al emitir), el acopio se amplía a lo utilizado
        // y la diferencia queda pendiente de pago. Al quedar sin crédito pendiente, no admite más entregas.
        // increaseHoardValue recibe el monto con recargo (lo que paga el cliente), igual que la ampliación manual
        const extendedValue = getExtendedValue({ ...loadedHoard, items: newItems })
        if (extendedValue) {
            await increaseHoardValue(hoardId, extendedValue.addedAmount)
        }

        for (const item of items) {
            await reserveStock(item.productId, loadedHoard.branchId, item.quantity, item.lotId)
        }

        const deliveryOrderItemsData = items.map(item => {
            const initialItem = findInitialItem(loadedHoard.items, item)
            return {
                itemId: initialItem?.id || item.id,
                quantity: item.quantity
            }
        })
        await emitDeliveryOrder(deliveryOrderData, deliveryOrderItemsData)
    }

    // Corrects the price of a product in a financial hoard prices list. The server plans it again with the current hoard
    // (the modal shows the same planHoardPriceCorrection as a preview) and saves the fiscal note of the invoiced units
    // and the hoard in a single transaction. The balance the delivered units consumed above the correct price comes back
    // through the corrected item prices, so that note doesn't adjust the hoard
    const correctHoardPrice = async (
        hoardId: string,
        productId: string,
        newPrice: number
    ) => {
        const result = await correctServerHoardPrice(hoardId, productId, newPrice)
        const { loadHoard } = hoardLoaders()
        await loadHoard(hoardId, true)
        return result
    }

    // addCreditInFavor solo aplica a acopios financieros con crédito pendiente
    const closeHoard = async (hoardId: string, addCreditInFavor = false) => {
        const { loadHoard } = hoardLoaders()
        const hoard = (await loadHoard(hoardId, true))!

        // Se revalida contra la base por si la facturación cambió mientras se tenía abierto el detalle.
        // Cerrar dejaría comprobantes por productos que nunca se entregaron.
        const deliveryOrders = await deliveryOrderLoaders().loadOperationDeliveryOrders(hoardId, false)
        if (hoardHelpers.hasInvoicedPendingItems(hoard, deliveryOrders)) {
            throw new Error('Hay productos facturados sin entregar. Entregalos o generá una nota de crédito antes de cerrar.')
        }

        if (hoard.type === HoardType.PRODUCT) {
            const diffMap = new Map<string, Partial<ItemDeliveryStats>>()
            const pendingItems = hoard.items.filter(item => item.deliveryStats.pending > 0)
            pendingItems.forEach(item => {
                diffMap.set(item.id, {
                    pending: -item.deliveryStats.pending,
                    toBeEmitted: -item.deliveryStats.pending
                })
            })
            for (const item of pendingItems) {
                await enableStock(item.productId, hoard.branchId, item.deliveryStats.pending, item.lotId)
            }
            return updateDeliveryStats(TableName.HOARD_ORDERS, hoardId, diffMap, {
                deliveryStatus: DeliveryStatus.DELIVERED
            }) as Promise<HoardOrder>
        } else {
            const { pendingCredit, noteAmount } = getClosingCredit(hoard)
            if (pendingCredit <= 0) {
                return updateSaleOperationProps(TableName.HOARD_ORDERS, hoardId, {
                    deliveryStatus: DeliveryStatus.DELIVERED
                })
            }

            let description = `Cierre con crédito pendiente de ${formatPrice(pendingCredit)} sin acreditar`
            if (addCreditInFavor) {
                // La nota baja lo adeudado a lo utilizado; lo pagado por encima de eso pasa al crédito a favor del cliente
                const savedNote = await createUnusedHoardCreditNote(hoardId, noteAmount)
                const creditInFavor = await applyAdjustmentNote(TableName.HOARD_ORDERS, hoardId, savedNote.amount)
                description = creditInFavor > 0 ?
                    `Cierre con crédito pendiente de ${formatPrice(pendingCredit)}: ${formatPrice(creditInFavor)} acreditados como crédito a favor` :
                    `Cierre con crédito pendiente de ${formatPrice(pendingCredit)}: lo adeudado se redujo a lo utilizado`
            }

            return updateSaleOperationProps(TableName.HOARD_ORDERS, hoardId, {
                deliveryStatus: DeliveryStatus.DELIVERED,
                unusedCredit: round((hoard.unusedCredit || 0) + pendingCredit),
                events: addEvent(hoard, description)
            } as Partial<HoardOrder>)
        }
    }

    // Amplía un acopio financiero manteniendo su lista de precios. addedAmount es lo que paga el cliente
    // (con recargo e IVA), igual que al crear el acopio. Fuera de cuenta corriente se llama al confirmar el cobro.
    const increaseHoardValue = async (
        hoardId: string,
        addedAmount: number
    ) => {
        const { loadHoard } = hoardLoaders()
        const loadedHoard = (await loadHoard(hoardId, true))!

        const hoardWithNewValue = getIncreasedHoard(loadedHoard, addedAmount)

        const total = getPriceOutline(TableName.HOARD_ORDERS, hoardWithNewValue).total
        const { paidAmount } = loadedHoard
        const paymentStatus = paidAmount >= round(total) ?
            PaymentStatus.PAYED :
            paidAmount > 0 ? PaymentStatus.PARTIALLY_PAYED : PaymentStatus.PENDING

        const updatedHoard = buildHoard({
            ...hoardWithNewValue,
            paymentStatus,
            events: addEvent(loadedHoard, `Ampliación de ${formatPrice(addedAmount)}`)
        })
        const savedHoard = await saveHoard(updatedHoard)

        // Si ya estaba facturado, la ampliación se respalda con una nota de débito (queda pendiente de emitir)
        const isInvoiced = [FiscalDocumentStatus.AUTHORIZED, FiscalDocumentStatus.TO_AUTHORIZE].includes(loadedHoard.invoiceStatus)
        if (isInvoiced) {
            await createDebitNote(
                TableName.HOARD_ORDERS,
                hoardId,
                `Ampliación de Acopio ${describeCode(loadedHoard)}`,
                addedAmount,
                loadedHoard.vatTaxYield
            )
        }

        return savedHoard
    }

    const archiveHoard = async (hoardId: string) => {
        const { loadHoard } = hoardLoaders()
        const hoard = (await loadHoard(hoardId, true))!

        // Se revalida contra la base: un acopio con saldo o facturación abierta no se archiva.
        const isInvoiceClosed = hoard.invoiceStatus === FiscalDocumentStatus.AUTHORIZED
        const canArchive = hoard.paymentStatus === PaymentStatus.PAYED &&
            hoard.deliveryStatus === DeliveryStatus.DELIVERED &&
            isInvoiceClosed &&
            !hoard.archived
        if (!canArchive) {
            throw new Error('El acopio debe estar pago, entregado y con la facturación cerrada para archivarse.')
        }

        return archiveSaleOperation(TableName.HOARD_ORDERS, hoardId)
    }

    // Solo marca el acopio como cancelado: los listados y la deuda de cuenta corriente ya excluyen los cancelados
    const cancelFinancialHoard = async (hoardId: string) => {
        const { loadHoard } = hoardLoaders()
        const hoard = (await loadHoard(hoardId, true))!
        const deliveryOrders = await deliveryOrderLoaders().loadOperationDeliveryOrders(hoardId, false)

        // Se revalida contra la base por si el acopio cambió mientras se tenía abierto el detalle
        if (!hoardHelpers.isFinancialHoardCancellable(hoard, deliveryOrders)) {
            throw new Error('El acopio ya no se puede cancelar')
        }

        return updateSaleOperationProps(TableName.HOARD_ORDERS, hoardId, { cancelled: true })
    }

    return {
        createHoard,
        updateHoard,
        emitFinancialDeliveryOrder,
        correctHoardPrice,
        closeHoard,
        increaseHoardValue,
        archiveHoard,
        cancelFinancialHoard
    }
}
